/**
 * Dates, durées et pourcentages.
 *
 * L'API rend des instants ISO 8601 en UTC ; l'affichage se fait dans le fuseau
 * du navigateur, parce qu'un utilisateur lit l'heure à laquelle il a acheté, pas
 * celle du serveur.
 */

const DATE_COURTE = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const DATE_HEURE = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** `"2026-03-14T08:12:00Z"` → `"14 mars 2026"`. */
export function formatDate(iso: string): string {
  return DATE_COURTE.format(new Date(iso));
}

/** `"2026-03-14T08:12:00Z"` → `"14/03/2026 09:12"` (fuseau du navigateur). */
export function formatDateTime(iso: string): string {
  return DATE_HEURE.format(new Date(iso));
}

/**
 * Valeur à mettre dans l'attribut `dateTime` d'un `<time>`, telle quelle.
 *
 * Un `<time dateTime="…">` rend la date lisible par une machine — et par un
 * lecteur d'écran qui préfère la forme longue à `"14/03"`.
 */
export function isoDateTime(iso: string): string {
  return new Date(iso).toISOString();
}

const RELATIF = new Intl.RelativeTimeFormat("fr-FR", { numeric: "auto" });

/** Les paliers de `formatRelatif`, du plus fin au plus large, en secondes. */
const PALIERS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ["minute", 60],
  ["hour", 3600],
  ["day", 86_400],
  ["month", 30 * 86_400],
  ["year", 365 * 86_400],
];

/**
 * `"2026-10-14T08:00:00Z"` → `"il y a 3 jours"`, mesuré depuis `maintenant`.
 *
 * Arrondi au palier le plus large qui tienne : « il y a 2 heures » plutôt que
 * « il y a 127 minutes ». Sous une minute : « à l'instant ». La date exacte
 * reste à afficher à côté (infobulle, `<time dateTime>`) : ce libellé dit
 * l'ancienneté, pas l'instant.
 */
export function formatRelatif(iso: string, maintenant: Date = new Date()): string {
  const ecart = (new Date(iso).getTime() - maintenant.getTime()) / 1000;
  const absolu = Math.abs(ecart);
  if (absolu < 60) {
    return "à l'instant";
  }
  let unite: Intl.RelativeTimeFormatUnit = "minute";
  let taille = 60;
  for (const [u, t] of PALIERS) {
    if (absolu >= t) {
      unite = u;
      taille = t;
    }
  }
  return RELATIF.format(Math.round(ecart / taille), unite);
}

/**
 * Durée en jours-homme → `"1 jour"`, `"12 jours"`.
 *
 * Les jours du contrat sont un nombre JSON borné, pas une chaîne : la
 * conversion est exacte, aucune précaution `BigInt` n'est nécessaire ici.
 *
 * Rien à composer en heures ni en fractions : une prestation se commande en
 * jours entiers, et le seul accord à faire est celui du pluriel.
 */
export function formatDays(jours: number): string {
  return jours <= 1 ? `${jours} jour` : `${jours} jours`;
}

/** `42` → `"42 %"`, avec l'espace insécable que la typographie française exige. */
export function formatPercent(valeur: number): string {
  return `${Math.round(valeur)} %`;
}
